import React, { ReactElement, ReactNode } from 'react';
import { Radio as AntRadio, RadioChangeEvent } from 'antd';
import Radio from './Radio';
import styled from 'styled-components';

type RadioGroupDirection = 'row' | 'column';

interface CustomStyles {
  gap?: CSSSize;
  addonAfterPaddingLeft?: CSSSize;
}

interface RadioGroupProps<ValueType> {
  options: { value: ValueType; label: string; addonAfter?: ReactNode }[];
  value: ValueType;
  onChange: (value: ValueType) => void;
  direction?: RadioGroupDirection;
  children?: ReactNode;
  customStyles?: CustomStyles;
}

function RadioGroup<ValueType>({
  options,
  value,
  direction = 'row',
  onChange,
  children,
  customStyles,
}: RadioGroupProps<ValueType>): ReactElement {
  const antOnChange = (e: RadioChangeEvent) => {
    onChange && onChange(e.target.value as ValueType);
  };

  return (
    <StyledRadioGroup
      direction={direction}
      customstyles={customStyles}
      value={value}
      onChange={antOnChange}
    >
      {children ||
        options.map((item, index) => {
          return (
            <Radio key={index} value={item.value}>
              <Wrapper>
                {item.label}
                {item.addonAfter && (
                  <AddonAfterWrapper $paddingLeft={customStyles?.addonAfterPaddingLeft}>
                    {item.addonAfter}
                  </AddonAfterWrapper>
                )}
              </Wrapper>
            </Radio>
          );
        })}
    </StyledRadioGroup>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 0;
  max-height: 40px;
`;
const AddonAfterWrapper = styled.div<{ $paddingLeft?: CSSSize }>`
  padding-left: ${props => props.$paddingLeft ?? '12px'};
`;

const StyledRadioGroup = styled(AntRadio.Group)<{
  direction: RadioGroupDirection;
  customstyles?: CustomStyles;
}>`
  &.ant-radio-group {
    display: flex;
    flex-direction: ${props => (props.direction === 'row' ? 'row' : 'column')};
    gap: ${props => props.customstyles?.gap};
  }
`;

export { RadioGroup };
